/**
 * Módulo puro (testável) para:
 *  - detetar erros injetados pelo WebView do Instagram/Facebook (bridge externa,
 *    não nossa) e filtrá-los apenas com assinaturas exatas;
 *  - remover identificadores sensíveis das URLs enviadas para telemetria.
 *
 * Regras:
 *  - NUNCA filtrar globalmente "postMessage" ou "window.webkit".
 *  - Só filtrar quando o user-agent é WebView Instagram/Facebook E a assinatura
 *    exata da bridge externa está presente na mensagem ou nos frames.
 *  - "Invariant failed" nunca é filtrado.
 */

/** Assinaturas exatas de código injetado pelo in-app browser da Meta. */
const INJECTED_FRAME_SIGNATURES = [
  "iabjs://navigation_performance_logger_android",
  "navigation_performance_logger_android",
  "fbnavinp",
  "fbnavigationperformancelogger",
  "iabjs.js",
] as const;

/** Mensagens conhecidas, mas só válidas em conjunto com UA Instagram/Facebook. */
const INJECTED_MESSAGE_SIGNATURES = [
  "iabjs",
  "fbnavinp",
  "navigation_performance_logger_android",
] as const;

const NEVER_FILTER = [/invariant failed/i];

export function isMetaWebView(userAgent: string | null | undefined): boolean {
  if (!userAgent) return false;
  return /\b(Instagram|FBAN|FBAV|FB_IAB|FBIOS)\b/i.test(userAgent);
}

export type InjectedErrorInput = {
  message?: string | null;
  frames?: (string | null | undefined)[];
  userAgent?: string | null;
};

export function isInjectedMetaBridgeError(input: InjectedErrorInput): boolean {
  const message = (input.message ?? "").toLowerCase();
  if (NEVER_FILTER.some((re) => re.test(message))) return false;
  if (!isMetaWebView(input.userAgent)) return false;

  const frames = (input.frames ?? []).filter(Boolean).map((f) => String(f).toLowerCase());
  const frameHit = frames.some((f) => INJECTED_FRAME_SIGNATURES.some((sig) => f.includes(sig)));
  const messageHit = INJECTED_MESSAGE_SIGNATURES.some((sig) => message.includes(sig));
  return frameHit || messageHit;
}

/** Parâmetros de query removidos antes de enviar telemetria. */
const SENSITIVE_QUERY_PARAMS = [
  "fbclid",
  "gclid",
  "igshid",
  "igsh",
  "msclkid",
  "ttclid",
  "twclid",
  "mc_eid",
  "mc_cid",
  "email",
  "token",
  "access_token",
  "refresh_token",
  "code",
  "session",
  "rt_aid",
];

/** UTM não sensíveis mantidos. */
const ALLOWED_PREFIXES = ["utm_"];

export function sanitizeTelemetryUrl(url: string | null | undefined): string | null {
  if (!url) return url ?? null;
  try {
    const parsed = new URL(url, "https://placeholder.invalid");
    for (const key of [...parsed.searchParams.keys()]) {
      const lower = key.toLowerCase();
      const allowed = ALLOWED_PREFIXES.some((p) => lower.startsWith(p));
      if (!allowed && SENSITIVE_QUERY_PARAMS.includes(lower)) parsed.searchParams.delete(key);
    }
    parsed.hash = "";
    const out = parsed.origin === "https://placeholder.invalid"
      ? `${parsed.pathname}${parsed.search}`
      : parsed.toString();
    return out;
  } catch {
    return url.split("?")[0] ?? url;
  }
}

/** Contador agregado de erros externos suprimidos (log técnico, sem alerta). */
let suppressedCount = 0;
export function noteSuppressedInjectedError(): number {
  suppressedCount += 1;
  return suppressedCount;
}
export function getSuppressedInjectedErrorCount(): number {
  return suppressedCount;
}
export function resetSuppressedInjectedErrorCount(): void {
  suppressedCount = 0;
}
